import { deleteAsync, getAsync, postAsync, putAsync } from '../../../../shared/api.service'
import { queryString } from '../../../../shared/sso-workspace/auth-api'

// The AI tools telemetry staff APIs (spec 2026-10-20 §9; contract §4.5,
// §4.6): rapid-api-ai's /api/ddd/ai-tools/admin/... reads and the marks
// write, called straight at REACT_APP_API_AI with the staff session (never
// through the gateway: these take no API key). Like the content and notify
// direct-call modules: plain functions, each resolving to the response body
// and, on failure, throwing the response ({ status, data: { code, message
// } }), so serverMessage(err) reads the server's sentence and
// <ErrorReference error={err} /> its request id. Being module functions,
// they keep one identity for the life of the app, unlike api-new's hooks
// (whose returned object is new on every request-state change), so they are
// safe in any effect's or memo's deps.
//
// Reads need SYS_AI_TOOLS_TELEMETRY read; the marks write needs update; the
// recycle bin's writes (delete, restore, permanently delete, empty) need
// delete. Refusals: 400 ai_tools.bad_query, 401 ai_tools.signed_out, 403
// ai_tools.forbidden, 404 ai_tools.not_found (one record), 413
// ai_tools.too_many_matches (a selection over 10,000 rows: nothing written).
const base = () => process.env.REACT_APP_API_AI

// Whether this build knows where the ai service is.
export const aiToolsAvailable = () => Boolean(base())

const unwrap = async call => {
  try {
    const result = await call()
    return result ? result.data : undefined
  } catch (err) {
    throw (err && err.response) || err
  }
}

const pathID = value => encodeURIComponent(String(value))
const get = (path, params) => unwrap(() => getAsync(base(), `api/ddd/ai-tools/${path}${queryString(params)}`))

// Lists answer { items, next }: newest first (a session's records oldest
// first); `next` is the following page's ?cursor=, null on the last. Every
// list but a session's records also takes login_id (one person's rows), and
// every one q (a search: trimmed, case-insensitive, at most 200 characters).
// A row carries person: { loginId, name, email } | null, and a binned one
// deletedAt and deletedBy.
// Sessions: ?source&channel&organization_id&consumer&status&login_id&q&limit(1..200)&cursor
export const listSessions = params => get('admin/sessions', params)
// { session: { ...the list's row, header } }
export const getSession = sessionID => get(`admin/sessions/${pathID(sessionID)}`)
// A session's records, oldest first: ?kind&q&limit(1..500)&cursor
export const listSessionEvents = (sessionID, params) => get(`admin/sessions/${pathID(sessionID)}/events`, params)
// ?organization_id&consumer&status&limit&cursor
export const listPostmortems = params => get('admin/postmortems', params)
// { postmortem: { ...row, body }, findings: [{ ...finding, body, marks }] }
export const getPostmortem = postmortemID => get(`admin/postmortems/${pathID(postmortemID)}`)
// ?severity&category&tool&consumer&status&limit&cursor
export const listFindings = params => get('admin/findings', params)
// { consumers: ['prompt-refinement', 'triage'] }: every consumer that has marked anything
export const listConsumers = () => get('admin/consumers')
// { clientApps: ['Claude Code', 'Codex App'] }: the client apps live sessions name (their header's clientApp)
export const listClientApps = () => get('admin/client-apps')
// A person's AI tools sharing answers (consent visibility, owner-approved
// 2026-10-23): { loginId, consentStatus: 'sharing' | 'not_sharing' |
// 'never_asked' | 'account_deleted',
// current, minQuestionVersion, history (newest first, at most 100),
// historyMore, organizations: [{ organizationId, name, personal,
// allowsSharing }] }; an answer is { choice, questionVersion, answeredAt,
// source, clientVersion, organizationId, organizationName }. 404 for no such login.
export const getLoginConsent = loginID => get('admin/consent', { login_id: loginID })

// One mark as the server takes it (contract §4.5): a numeric id; a note only
// when there is one, and never with "unprocessed" (which removes the
// consumer's mark).
export const markOf = ({ consumer, kind, id, status, note }) => ({ consumer, target: { kind, id: Number(id) }, status, ...noteOf(status, note) })
const noteOf = (status, note) => {
  const text = String(note || '').trim()
  return text && status !== 'unprocessed' ? { note: text } : {}
}
// The mark of the filter form: { consumer, status, note? }.
export const markFieldsOf = ({ consumer, status, note }) => ({ consumer, status, ...noteOf(status, note) })

// PUT /processing { marks } (at most 500) -> { marked, cleared, missing: [{ kind, id }] }
export const putMarks = marks => unwrap(() => putAsync(base(), 'api/ddd/ai-tools/processing', { marks }))

// "Mark all matching…": PUT /processing { filter, mark, dryRun? } -> { matched,
// marked, cleared, missing, dryRun? }. The filter is what the lists and the
// export take (words.matchFilter) plus an optional upTo; 413
// ai_tools.too_many_matches when more than 10,000 rows match (nothing written).
// except: ids the filter matches that are left out (select mode's "all
// matching except these").
export const putMarkFilter = (filter, mark, { dryRun = false, except } = {}) =>
  unwrap(() => putAsync(base(), 'api/ddd/ai-tools/processing', {
    filter,
    ...(except && except.length ? { except } : {}),
    mark,
    ...(dryRun ? { dryRun: true } : {})
  }))

// The recycle bin (R2, owner-approved 2026-10-29). A selection is { ids } (1
// to 10,000) or { filter, except? } (the lists' filter: matchFilter, with an
// upTo snapshot), of one kind: 'session' | 'event' | 'postmortem' | 'finding'.
// POST /admin/recycle { action: 'delete' | 'restore' | 'purge', kind,
// ...selection, dryRun? } -> { matched, changed, dryRun }. Delete moves live
// rows to the bin (their records, postmortems and findings go with them,
// unstamped, so a restore brings everything back); restore and purge take
// rows binned on their own. dryRun counts and writes nothing.
export const recycleWrite = (action, kind, selection, { dryRun = false } = {}) =>
  unwrap(() => postAsync(base(), 'api/ddd/ai-tools/admin/recycle', { action, kind, ...selection, ...(dryRun ? { dryRun: true } : {}) }))
// The bin, newest deleted first: ?kind&q&limit&cursor -> { items, next }; an
// item is its kind's list row with deletedAt and deletedBy { loginId, name,
// email } | null.
export const listRecycle = params => get('admin/recycle', params)
// Empty the bin (of one kind, or every kind): -> { purged: { sessions,
// events, postmortems, findings } }.
export const emptyRecycle = kind => unwrap(() => deleteAsync(base(), `api/ddd/ai-tools/admin/recycle${queryString(kind ? { kind } : {})}`))

// The staff export, a page at a time: { sessions | postmortems, next, asOf }.
// ?organization_id&source&consumer&status&from&to&limit(sessions 1..50, postmortems 1..100)&cursor
export const exportSessions = params => get('export/sessions', params)
export const exportPostmortems = params => get('export/postmortems', params)
